import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { isDateAvailable } from '@/lib/helpers/helpers';
import { endOfDay, startOfDay } from 'date-fns';
import { selectClientStorage } from '@/state/clientStorage/clientStorageSelectors';
import { selectMultiplePullStatusByAccountId } from '@/state/exchanges/exchangeSelectors';
import { initialDate } from '@/state/clientStorage/clientStorageSlice';
import { MultipleAccountsRequestsPayload } from '@/state/legs/legsTypes';
import { symbolsListQuery } from '@/api/queries/symbolsListQuery';
import { SymbolsFilterItem } from '@/state/legs/legsTypes';

const EMPTY_SYMBOLS_LIST: SymbolsFilterItem[] = [];

interface UseSymbolsListQueryProps {
    accountIds?: number[];
    singleAccountId?: number;
}

const useSymbolsListQuery = ({ accountIds = [], singleAccountId }: UseSymbolsListQueryProps) => {
    const allFilters = useSelector((state: RootState) => selectClientStorage(state));
    const relevantPulls = useSelector((state: RootState) =>
        selectMultiplePullStatusByAccountId(state, {
            accountIds: singleAccountId ? [singleAccountId] : accountIds,
        }),
    );

    const isCompleted = useMemo(
        () => relevantPulls.some((pull) => pull === 'completed'),
        [relevantPulls],
    );

    const dateSelection = useMemo(() => {
        if (accountIds.length > 0) {
            return allFilters.generalDateSelection || initialDate;
        }
        if (singleAccountId) {
            return allFilters.accountDateSelection[singleAccountId] || initialDate;
        }
        return initialDate;
    }, [
        accountIds,
        singleAccountId,
        allFilters.generalDateSelection,
        allFilters.accountDateSelection,
    ]);

    const params = useMemo((): MultipleAccountsRequestsPayload => {
        const { label = '' } = dateSelection;
        const ids = singleAccountId ? [singleAccountId] : accountIds;

        if (!isDateAvailable(label)) {
            return {
                accountIds: ids,
                startDate: startOfDay(new Date('01/01/2016')).toISOString(),
                endDate: endOfDay(new Date()).toISOString(),
            };
        }

        return {
            accountIds: ids,
            startDate: dateSelection.startDate,
            endDate: dateSelection.endDate,
        };
    }, [dateSelection, accountIds, singleAccountId]);

    const query = useQuery({
        ...symbolsListQuery(params, isCompleted),
        enabled: accountIds.length > 0 || !!singleAccountId,
    });

    return {
        symbolsList: query.data ?? EMPTY_SYMBOLS_LIST,
        isLoading: query.isLoading,
    };
};

export default useSymbolsListQuery;
